/// <reference lib="dom" />
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'

import { createThemeFactory } from './ThemeFactory'
import { AppTheme } from './Themes'

const detectAppType = (): 'batikal' | 'construkal' => {
  if (typeof process !== 'undefined' && process.env.EXPO_PUBLIC_APP_TYPE === 'construkal') {
    return 'construkal'
  }
  return 'batikal'
}

/** Web: prefers-color-scheme (no react-native). */
function useWebColorScheme(): 'light' | 'dark' | null {
  const [scheme, setScheme] = useState<'light' | 'dark' | null>(() => {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => setScheme(mq.matches ? 'dark' : 'light')
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return scheme
}

interface AppThemeContextType {
  theme: AppTheme
  appType: 'batikal' | 'construkal'
  isDark: boolean
  isLight: boolean
}

const AppThemeContext = createContext<AppThemeContextType | undefined>(undefined)

interface AppThemeProviderProps {
  children: ReactNode
}

export const AppThemeProvider = ({ children }: AppThemeProviderProps) => {
  const colorScheme = useWebColorScheme()
  const [appType, setAppType] = useState<'batikal' | 'construkal'>('batikal')

  useEffect(() => {
    setAppType(detectAppType())
  }, [])

  const themeFactory = createThemeFactory(appType)
  const theme = themeFactory.getTheme(colorScheme === 'dark' ? 'dark' : 'light')

  const value: AppThemeContextType = {
    theme,
    appType,
    isDark: colorScheme === 'dark',
    isLight: colorScheme === 'light'
  }

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>
}

export const useAppTheme = (): AppThemeContextType => {
  const context = useContext(AppThemeContext)
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider')
  }
  return context
}
